// src/data/bulletinBoard.js
//
// THE BULLETIN BOARDS. Ruling 173 (2026-09-17), from Bob's answer to the master list's question Q1,
// given verbatim in this session:
//
//   "General Chairman make the posts, only the general chairman can remove them. Anyone can copy
//   digitally. LC can post from their own bulletin boards those things that matter to their specific
//   member / local (gca) members"
//
//   "GC full authority to post, edit, and remove from his own board and that of all bulletine boards
//   within her general committee, the lcs only authority and responsibility is limited to their own
//   posts"
//
// ---------------------------------------------------------------------------------------------
// THE SHAPE, AND WHY IT IS TWO BOARD KINDS AND ONE POST COLLECTION.
//
// There are boards at two levels because Bob named two: the General Chairperson's board, and the
// Local Chairperson's own board carrying "those things that matter to their specific member / local
// (gca) members". A board is not a document - it is an ADDRESS, and the address is one that already
// exists everywhere on this platform: a committee id (GO-342) for a committee board, a scopeKey
// (LC1951-342C) for a local board. Inventing a boards collection would put a second name on a thing
// the office spine already names, which is directives XI.
//
// Every post therefore carries BOTH its boardId and its committee. The committee is not redundant:
// it is the whole of the General Chairperson's reach. "All bulletin boards within her general
// committee" is `committee == token.committee`, and a local board that did not carry its committee
// could not be reached by the officer Bob says reaches it.
//
// ---------------------------------------------------------------------------------------------
// THE AUTHORITY TABLE, WHICH IS BOB'S TWO SENTENCES AND NOTHING ADDED:
//
//   General Chairperson   post / edit / withdraw   ANY post on ANY board in his committee,
//                                                  whoever wrote it.
//   Local Chairperson     post                     to a local board he is scoped to.
//                         edit / withdraw          HIS OWN POSTS ONLY - never another LC's,
//                                                  never the GC's.
//   Everyone else         read, and copy.
//
// "Only the general chairman can remove them" is the GC half of that table read from the other end:
// an LC cannot remove a GC's post, because an LC's authority reaches his own posts and stops.
//
// ---------------------------------------------------------------------------------------------
// THREE DESIGN CHOICES TAKEN UNDER PD-19, EACH WITHDRAWABLE BY NUMBER (ruling 173):
//
//  1. REMOVAL IS WITHDRAWAL, NOT DELETION. Bob said "remove". Everywhere else on this platform a
//     thing that is removed is marked and kept: the War Room's rules say `allow delete: if false`,
//     ruling 30 segregates and does not destroy, RU-1 keeps a withdrawn ruling so its number still
//     points at something, VF-6 withdraws a finding in plain terms rather than letting it vanish.
//     A post that is removed stops appearing on the board and stays in the record with who removed
//     it and when. If Bob wants it gone, that is a different instruction and it is his to give.
//
//  2. ANNOUNCEMENTS ONLY IN V1 - THERE IS NO REPLY. Bob described posting, editing, removing and
//     copying. He described no reply, and ruling 153 is the precedent for not inventing an object
//     nobody asked for. A board that grows a comment thread grows a moderation duty with it, and
//     that duty was not assigned.
//
//  3. COPYING IS AN AFFORDANCE, NOT A PERMISSION. "Anyone can copy digitally" is built as an
//     explicit copy on a post the reader can already see. It does not widen who may READ a board -
//     a reader cannot copy what was never shown to him.
//
// Pure module: no React, no Firestore. The page draws and writes; this decides. Same split as
// src\data\warRoom.js, deliberately, so the two cannot drift apart in method.

const F = Object.freeze;

export const BOARD_KINDS = F({ COMMITTEE: 'committee', LOCAL: 'local' });
export const STATES = F({ POSTED: 'posted', WITHDRAWN: 'withdrawn' });

export const TITLE_MAX = 140;
export const BODY_MAX = 8000;

function str(v) { return typeof v === 'string' ? v.trim() : ''; }
function isIso(s) { return typeof s === 'string' && !Number.isNaN(Date.parse(s)); }

// ---------------------------------------------------------------------------------------------
// ADDRESSING

// A board is named by the office that owns it. A committee board is addressed by the committee id;
// a local board by the scopeKey. Nothing else is a board.
export function boardId(kind, key) {
  const k = str(key);
  if (!k) return null;
  if (kind === BOARD_KINDS.COMMITTEE || kind === BOARD_KINDS.LOCAL) return k;
  return null;
}

// Every board a user may SEE, in the order a sidebar should show them: his committee's board first,
// then the local boards he is scoped to. An officer's own board is in here by the same rule as
// anyone else's - there is no separate "my board" concept, only authority over one.
export function boardsForUser(user) {
  if (!user) return [];
  const out = [];
  const committee = str(user.committee);
  if (user.isAdmin) {
    // The administrator reaches every screen (roleScreens.js). He sees the committee he is looking
    // at, if he names one; he is given no implicit committee here, because he holds none.
    if (committee) out.push({ id: committee, kind: BOARD_KINDS.COMMITTEE, committee, label: committee + ' - committee board' });
    for (const s of (Array.isArray(user.scopes) ? user.scopes : [])) {
      out.push({ id: s, kind: BOARD_KINDS.LOCAL, committee, label: s + ' - local board' });
    }
    return out;
  }
  if (!committee) return [];
  out.push({ id: committee, kind: BOARD_KINDS.COMMITTEE, committee, label: committee + ' - committee board' });
  for (const s of (Array.isArray(user.scopes) ? user.scopes : [])) {
    if (str(s)) out.push({ id: s, kind: BOARD_KINDS.LOCAL, committee, label: s + ' - local board' });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// AUTHORITY - the table above, and nothing beyond it.

// The General Chairperson's reach: every board in HIS committee, whatever its kind.
export function isGCOfBoard(user, board) {
  if (!user || !board) return false;
  if (user.isAdmin) return true;
  return user.role === 'general_chair'
    && !!str(user.committee)
    && str(user.committee) === str(board.committee);
}

// The Local Chairperson's reach: a local board whose scopeKey he holds. A committee board is never
// an LC's to post to - that is the General Chairperson's board.
export function isLCOfBoard(user, board) {
  if (!user || !board) return false;
  if (board.kind !== BOARD_KINDS.LOCAL) return false;
  return user.role === 'local_chair'
    && Array.isArray(user.scopes)
    && user.scopes.includes(board.id);
}

export function mayPost(user, board) {
  return isGCOfBoard(user, board) || isLCOfBoard(user, board);
}

// Edit and withdraw are ONE authority, because Bob gave them in one breath ("post, edit, and
// remove" / "limited to their own posts") and splitting them would invent a distinction he did not
// make. A GC reaches any post on any board in his committee. An LC reaches only what he wrote.
export function mayEditPost(user, board, post) {
  if (!user || !board || !post) return false;
  if (isGCOfBoard(user, board)) return true;
  if (!isLCOfBoard(user, board)) return false;
  return !!user.uid && post.postedBy === user.uid;
}

export const mayWithdrawPost = mayEditPost;

// Why a reader may not act, in words a person can read. Never a bare false.
export function refusalToEdit(user, board, post) {
  if (!user || !board || !post) return 'This post cannot be opened.';
  if (user.role === 'local_chair' && isLCOfBoard(user, board) && post.postedBy !== user.uid) {
    return 'A Local Chairperson may edit and remove his own posts. This one was posted by '
      + (str(post.postedByName) || 'another officer') + '.';
  }
  if (user.role === 'local_chair' && board.kind === BOARD_KINDS.COMMITTEE) {
    return 'The committee board belongs to the General Chairperson.';
  }
  return 'Posting to this board is the office holder’s.';
}

// ---------------------------------------------------------------------------------------------
// THE POST

export function composePost(user, board, input) {
  const i = input || {};
  const errors = [];
  if (!mayPost(user, board)) errors.push(refusalToEdit(user, board, { postedBy: null }));
  const title = str(i.title);
  const body = str(i.body);
  if (!title) errors.push('A post has a title.');
  if (title.length > TITLE_MAX) errors.push('The title is limited to ' + TITLE_MAX + ' characters.');
  if (!body) errors.push('A post with no body is not a notice.');
  if (body.length > BODY_MAX) errors.push('The body is limited to ' + BODY_MAX + ' characters.');
  if (errors.length) return { ok: false, errors, record: null };
  return {
    ok: true,
    errors: [],
    record: {
      boardId: board.id,
      boardKind: board.kind,
      committee: board.committee,
      title,
      body,
      state: STATES.POSTED,
      postedBy: user.uid,
      postedByName: str(user.name) || str(user.email) || 'Unknown',
      postedByRole: user.isAdmin ? 'admin' : str(user.role),
      postedAt: i.at || new Date().toISOString(),
      editedAt: null,
      editedBy: null,
      withdrawnAt: null,
      withdrawnBy: null,
      withdrawnByName: null,
      withdrawnReason: null,
    },
  };
}

// An edit changes the words and says so. It never changes who posted it, when, or which board it is
// on - a post that could move boards or change hands is not the same notice.
export function editPost(user, board, post, input) {
  const i = input || {};
  const errors = [];
  if (!mayEditPost(user, board, post)) errors.push(refusalToEdit(user, board, post));
  if (post && post.state === STATES.WITHDRAWN) errors.push('A removed post is not edited. Post a new notice.');
  const title = str(i.title);
  const body = str(i.body);
  if (!title) errors.push('A post has a title.');
  if (title.length > TITLE_MAX) errors.push('The title is limited to ' + TITLE_MAX + ' characters.');
  if (!body) errors.push('A post with no body is not a notice.');
  if (body.length > BODY_MAX) errors.push('The body is limited to ' + BODY_MAX + ' characters.');
  if (errors.length) return { ok: false, errors, patch: null };
  return {
    ok: true,
    errors: [],
    patch: { title, body, editedAt: i.at || new Date().toISOString(), editedBy: user.uid },
  };
}

// Removal is withdrawal (design choice 1). The post leaves the board and stays in the record.
export function withdrawPost(user, board, post, input) {
  const i = input || {};
  const errors = [];
  if (!mayWithdrawPost(user, board, post)) errors.push(refusalToEdit(user, board, post));
  if (post && post.state === STATES.WITHDRAWN) errors.push('This post has already been removed.');
  if (errors.length) return { ok: false, errors, patch: null };
  return {
    ok: true,
    errors: [],
    patch: {
      state: STATES.WITHDRAWN,
      withdrawnAt: i.at || new Date().toISOString(),
      withdrawnBy: user.uid,
      withdrawnByName: str(user.name) || str(user.email) || 'Unknown',
      withdrawnReason: str(i.reason) || null,
    },
  };
}

// ---------------------------------------------------------------------------------------------
// READING THE BOARD

// What is ON the board: posted notices, newest first. A withdrawn post is not on the board.
// A post with no readable postedAt sorts LAST rather than first - the same rule deskQueue.js uses,
// because an unknown must never be rendered as a confident value (PD-4).
export function postsOnBoard(posts, board) {
  const list = Array.isArray(posts) ? posts : [];
  const mine = list.filter(p => p && p.boardId === (board && board.id) && p.state === STATES.POSTED);
  const dated = mine.filter(p => isIso(p.postedAt));
  const undated = mine.filter(p => !isIso(p.postedAt));
  dated.sort((a, b) => Date.parse(b.postedAt) - Date.parse(a.postedAt));
  return dated.concat(undated);
}

// The removed ones, for the officer who may see how the board got the way it is. Not on the board.
export function withdrawnOnBoard(posts, board) {
  const list = Array.isArray(posts) ? posts : [];
  return list.filter(p => p && p.boardId === (board && board.id) && p.state === STATES.WITHDRAWN);
}

// Design choice 3: the copy. Plain text of a post the reader is already looking at - no permission
// is consulted here, because the caller could not have this post in hand without one.
export function copyText(post, board) {
  if (!post) return '';
  const lines = [];
  lines.push(str(post.title));
  lines.push('');
  if (board && board.label) lines.push(board.label);
  lines.push('Posted by ' + (str(post.postedByName) || 'Unknown')
    + (isIso(post.postedAt) ? ' on ' + post.postedAt.slice(0, 10) : ''));
  if (post.editedAt) lines.push('Edited ' + String(post.editedAt).slice(0, 10));
  lines.push('');
  lines.push(str(post.body));
  return lines.join('\n');
}
